import { Heart, Users, Shield, Brain, Loader } from 'lucide-react';
import { useLegalContent } from '../../utils/legalContent';

const iconMap: Record<string, any> = {
  'Heart': Heart,
  'Shield': Shield,
  'Users': Users,
  'Brain': Brain
};

export const EthicsModal = () => {
  const { content, loading, error } = useLegalContent();

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader className="w-8 h-8 text-brand animate-spin" />
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="p-6 text-center">
        <p className="text-status-error">Failed to load AI ethics content.</p>
        <p className="text-th-secondary text-sm mt-2">
          Please check your legal-content.json file.
        </p>
      </div>
    );
  }

  const ethics = content.aiEthics;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Heart className="w-6 h-6 text-brand" />
          <h3 className="text-xl font-semibold text-th-primary">
            {ethics.title}
          </h3>
        </div>
        <p className="text-th-secondary">
          {ethics.subtitle}
        </p>
      </div>

      {/* Core Principles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {ethics.principles.map((principle, index) => {
          const IconComponent = iconMap[principle.icon] || Heart;
          return (
            <div key={index} className="card p-6">
              <IconComponent className={`w-8 h-8 ${principle.color} mb-4`} />
              <h4 className="text-lg font-semibold text-th-primary mb-2">
                {principle.title}
              </h4>
              <p className="text-th-secondary">
                {principle.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Detailed Ethics Policy */}
      <div className="card p-6 space-y-6">
        {ethics.sections.map((section, index) => (
          <div key={index}>
            <h4 className="text-lg font-semibold text-th-primary mb-3">
              {section.title}
            </h4>
            <p className="text-th-secondary leading-relaxed mb-3">
              {section.content}
            </p>
            {section.list && (
              <ul className="list-disc list-inside text-th-secondary space-y-1 ml-4">
                {section.list.map((item, itemIndex) => (
                  <li key={itemIndex}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Commitment Statement */}
      <div className="card p-6 bg-brand/10 border border-brand/20">
        <h4 className="text-lg font-semibold text-th-primary mb-3">
          {ethics.promise.title}
        </h4>
        <p className="text-th-secondary leading-relaxed">
          {ethics.promise.content}
        </p>
      </div>

      {/* Contact Information */}
      <div className="bg-elevated border border-subtle rounded-xl p-6">
        <h4 className="text-lg font-semibold text-th-primary mb-3">
          {ethics.contact.title}
        </h4>
        <p className="text-th-secondary mb-3">
          {ethics.contact.content}
        </p>
        <div className="text-th-primary">
          <p>Email: {ethics.contact.email}</p>
          {ethics.contact.committee && (
            <p>Ethics Committee: {ethics.contact.committee}</p>
          )}
        </div>
      </div>
    </div>
  );
};
